/**
 * VendorList Component
 *
 * Displays a list of vendors in a table view with filtering and actions.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';
import { escapeHtml } from '../../utils/sanitize.js';

// Map non-standard currency codes to ISO 4217 codes
const CURRENCY_CODE_MAP = {
  'DT': 'TND',   // Tunisian Dinar
  'DA': 'DZD',   // Algerian Dinar
  'DH': 'MAD',   // Moroccan Dirham
  'LE': 'EGP',   // Egyptian Pound
  'SR': 'SAR',   // Saudi Riyal
};

/**
 * Format currency
 */
function formatCurrency(amount, currency = null) {
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  let currencyCode = currency || getCurrency();
  currencyCode = CURRENCY_CODE_MAP[currencyCode] || currencyCode;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currencyCode} ${new Intl.NumberFormat(locale).format(amount)}`;
  }
}

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Render vendor table row
 */
function renderVendorRow(vendor) {
  return `
    <tr class="vendor-row" data-vendor-id="${vendor.id}">
      <td class="cell-name">
        <div class="vendor-name-cell">
          <a href="#vendors/${vendor.id}" class="vendor-link">${escapeHtml(vendor.name)}</a>
          ${vendor.aliases && vendor.aliases.length > 0 ? `
            <span class="vendor-aliases">${escapeHtml(vendor.aliases[0])}${vendor.aliases.length > 1 ? ` +${vendor.aliases.length - 1}` : ''}</span>
          ` : ''}
        </div>
      </td>
      <td class="cell-category">${escapeHtml(vendor.category) || '-'}</td>
      <td class="cell-email">${escapeHtml(vendor.contactInfo?.email) || '-'}</td>
      <td class="cell-value">${formatCurrency(vendor.totalContractValue || 0)}</td>
      <td class="cell-contracts">${vendor.activeContracts || 0}</td>
      <td class="cell-actions">
        <div class="action-buttons">
          <button class="btn-icon" onclick="window.viewVendor('${vendor.id}')" title="${t('common.view') || 'View'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button class="btn-icon" onclick="window.editVendor('${vendor.id}')" title="${t('common.edit') || 'Edit'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button class="btn-icon btn-danger" onclick="window.deleteVendor('${vendor.id}')" title="${t('common.delete') || 'Delete'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </td>
    </tr>
  `;
}

/**
 * Render filters
 */
function renderFilters(activeFilters = {}) {
  return `
    <div class="vendor-filters">
      <div class="filter-group">
        <label>${t('vendors.category') || 'Category'}</label>
        <select id="filter-category" class="filter-select" onchange="window.applyVendorFilters()">
          <option value="">${t('common.all') || 'All'}</option>
          <option value="technology" ${activeFilters.category === 'technology' ? 'selected' : ''}>${t('vendors.categories.technology') || 'Technology'}</option>
          <option value="services" ${activeFilters.category === 'services' ? 'selected' : ''}>${t('vendors.categories.services') || 'Services'}</option>
          <option value="supplies" ${activeFilters.category === 'supplies' ? 'selected' : ''}>${t('vendors.categories.supplies') || 'Supplies'}</option>
          <option value="consulting" ${activeFilters.category === 'consulting' ? 'selected' : ''}>${t('vendors.categories.consulting') || 'Consulting'}</option>
          <option value="other" ${activeFilters.category === 'other' ? 'selected' : ''}>${t('vendors.categories.other') || 'Other'}</option>
        </select>
      </div>
      <div class="filter-group filter-search">
        <label>${t('vendors.search') || 'Search'}</label>
        <input type="text" id="filter-search" class="filter-input" placeholder="${t('vendors.searchPlaceholder') || 'Search vendors...'}" value="${activeFilters.search || ''}" onkeyup="if(event.key === 'Enter') window.applyVendorFilters()" />
      </div>
      <div class="filter-group filter-actions">
        <button class="btn btn-secondary btn-sm" onclick="window.applyVendorFilters()">
          ${t('common.apply') || 'Apply'}
        </button>
        <button class="btn btn-secondary btn-sm" onclick="window.clearVendorFilters()">
          ${t('common.clear') || 'Clear'}
        </button>
      </div>
    </div>
  `;
}

/**
 * Render empty state
 */
function renderEmptyState() {
  return `
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="empty-icon">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
      <h3>${t('vendors.noVendors') || 'No vendors found'}</h3>
      <p>${t('vendors.noVendorsDescription') || 'Vendors are automatically created when you upload contracts or invoices, or you can add one manually.'}</p>
      <button class="btn btn-primary" onclick="window.createVendor()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        ${t('vendors.addVendor') || 'Add Vendor'}
      </button>
    </div>
  `;
}

/**
 * Render pagination
 */
function renderPagination(pagination) {
  const { page, totalPages, totalItems } = pagination;

  if (totalPages <= 1) return '';

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return `
    <div class="pagination">
      <span class="pagination-info">
        ${t('common.showing') || 'Showing'} ${(page - 1) * 20 + 1} - ${Math.min(page * 20, totalItems)} ${t('common.of') || 'of'} ${totalItems}
      </span>
      <div class="pagination-buttons">
        <button class="btn-page" ${page === 1 ? 'disabled' : ''} onclick="window.goToVendorPage(${page - 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        ${pages.map(p => `
          <button class="btn-page ${p === page ? 'active' : ''}" onclick="window.goToVendorPage(${p})">${p}</button>
        `).join('')}
        <button class="btn-page" ${page === totalPages ? 'disabled' : ''} onclick="window.goToVendorPage(${page + 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  `;
}

/**
 * Render the full vendor list component
 */
export function renderVendorList(options = {}) {
  const {
    vendors = [],
    filters = {},
    pagination = { page: 1, totalPages: 1, totalItems: 0 },
    showFilters = true,
  } = options;

  if (vendors.length === 0 && !showFilters) {
    return renderEmptyState();
  }

  return `
    <div class="vendor-list-container">
      <div class="vendor-list-header">
        <div class="header-left">
          <h2>${t('nav.vendors') || 'Vendors'}</h2>
          <span class="item-count">${pagination.totalItems} ${t('common.items') || 'items'}</span>
        </div>
        <div class="header-right">
          <button class="btn btn-primary" onclick="window.createVendor()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            ${t('vendors.addVendor') || 'Add Vendor'}
          </button>
        </div>
      </div>

      ${showFilters ? renderFilters(filters) : ''}

      ${vendors.length === 0 ? renderEmptyState() : `
        <div class="table-container">
          <table class="vendor-table">
            <thead>
              <tr>
                <th>${t('vendors.name') || 'Name'}</th>
                <th>${t('vendors.category') || 'Category'}</th>
                <th>${t('vendors.email') || 'Email'}</th>
                <th>${t('vendors.totalValue') || 'Total Value'}</th>
                <th>${t('vendors.activeContracts') || 'Contracts'}</th>
                <th>${t('common.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              ${vendors.map(vendor => renderVendorRow(vendor)).join('')}
            </tbody>
          </table>
        </div>
      `}

      ${renderPagination(pagination)}
    </div>
  `;
}

/**
 * Initialize vendor list event handlers
 */
export function initVendorListHandlers() {
  // No additional handlers needed
}

export { formatCurrency, formatDate };
